import { useContext } from "react";
import { useTheme } from "@mui/material";

import { EnergyUnitPrefixContext } from "@core/index";
import type { EnergyUnitPrefix } from "@core/index";

import BaseCard from "../../cards/BaseCard/BaseCard";
import HighchartsBase from "../HighchartsBase/HighchartsBase";
import { formatTooltipValue } from "../utils";
import { divisorForUnit, oneBelowUnit, resolveAxisUnit } from "./utils";

export type ComputedSeries = {
  series: number[]; // assumed base unit: kWh
  categories: string[];
};

type Last5YearsChartProps = {
  computedSeries: ComputedSeries;
  isLoading?: boolean;
  color?: string;
  title?: string;
  height?: number;
  /**
   * When `"k"`, force the axis/tooltip into kWh regardless of magnitude.
   * Falls back to EnergyUnitPrefixContext.
   */
  energyUnitPrefix?: EnergyUnitPrefix;
};

export function Last5YearsChart({
  computedSeries,
  isLoading = false,
  color = "secondary",
  title = "Last 5 Years",
  height = 200,
  energyUnitPrefix,
}: Last5YearsChartProps) {
  const theme = useTheme();
  const contextEnergyPrefix = useContext(EnergyUnitPrefixContext);
  const resolvedEnergyPrefix = energyUnitPrefix ?? contextEnergyPrefix;

  const seriesData = computedSeries.series ?? [];
  const maxKwh = seriesData.length ? Math.max(...seriesData) : 0;

  const axisUnit = resolveAxisUnit(maxKwh, resolvedEnergyPrefix);
  const axisDivisor = divisorForUnit(axisUnit);

  const points = seriesData.map((rawKwh) => ({
    y: rawKwh / axisDivisor,
    custom: { rawKwh },
  }));

  return (
    <BaseCard title={title} isCardRounded minHeight={200} sx={{ paddingLeft: 1, paddingRight: 1 }}>
      <HighchartsBase
        loading={isLoading}
        yUnit={axisUnit}
        disableExporting
        showGraphUnit={false}
        chart={{
          height,
          type: "column",
          zoomType: "",
          backgroundColor: "white",
        }}
        plotOptions={{
          column: {
            pointWidth: 10,
          },
        }}
        series={{
          data: points,
          color: theme.palette[color]?.main || theme.palette.secondary.main,
          name: "Energy Consumption",
        }}
        seriesOptions={{ marker: { enabled: false } }}
        width="100%"
        legend={{ enabled: false }}
        yAxis={{
          gridLineWidth: 0,
          tickAmount: 5,
          title: {
            text: axisUnit,
            rotation: 270,
            align: "middle",
            style: { fontSize: "12px", fontWeight: "300" },
          },
          labels: {
            style: { fontSize: "12px", fontWeight: "300" },
          },
        }}
        xAxis={{
          categories: computedSeries?.categories || [],
          labels: {
            style: { fontSize: "12px", fontWeight: "300" },
          },
        }}
        tooltip={{
          formatter: function () {
            const rawKwh = (this.point as any)?.custom?.rawKwh ?? 0;

            const tooltipUnit = oneBelowUnit(axisUnit);
            const tooltipDivisor = divisorForUnit(tooltipUnit);
            const tooltipValue = rawKwh / tooltipDivisor;

            const value = formatTooltipValue(tooltipValue, tooltipUnit);
            return `<b>${this.x}</b><br/>${value}`;
          },
        }}
      />
    </BaseCard>
  );
}
